import React, { useEffect } from "react";
import MaintenancePage from "./pages/MaintenancePage";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ProvisionKFS from "./pages/ProvisionKFS";
import Home from "./pages/Home";
import HomeGateway from "./pages/HomeGateway";
import ConsentPage from "./pages/ConsentPage";
import IndexRouting from "./pages/IndexRouting";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import BusinessInfo from "./pages/BusinessInfo";
import PersonalInfo from "./pages/PersonalInfo";
import BusinessInfoWhitespace from "./pageswhitespace/BusinessInfoWhitespace.tsx";
import IndexWhitespace from "./pageswhitespace/IndexWhitespace";
import HomeWhitespace from "./pageswhitespace/HomeWhitespace";
import ConsentWhitespace from "./pageswhitespace/ConsentWhitespace";
import CuratingOffersWhitespace from "./pageswhitespace/CuratingOffersWhitespace";
import PreApprovedAmount from "./pageswhitespace/PreApprovedAmount";

import ContactOwner from "./pages/ContactOwner";
import WaitingForUpdates from "./pages/WaitingForUpdates";
import AdditionalDetails from "./pages/AdditionalDetails";
import InternalRulesA from "./pages/InternalRulesA";
import InternalRulesB from "./pages/InternalRulesB";
import InternalRulesWhitespace from "./pageswhitespace/InternalRulesWhitespace";
import AllOffers from "./pages/AllOffers";
import LoanOffer from "./pages/LoanOffer";
import FinalOffer from "./pages/FinalOffer";
import LoanSuccess from "./pages/LoanSuccess";
import Welcome from "./pages/Welcome.tsx";
import SetupMandates from "./pages/SetupMandates.tsx";
import FinalKFS from "./pages/FinalKFS.tsx";
import SessionTimeout from "./pages/SessionTimeout.tsx";
import NotFound from "./pages/NotFound";
import VerificationDebug from "./pages/VerificationDebug";
import AdditionalLoanGST from "./pages/AdditionalLoanGST";
import LenderMockDecision from "./pages/LenderMockDecision";
import LenderErrorSimulation from "./pages/LenderErrorSimulation";
import LenderRejectedExploreMore from "./pages/LenderRejectedExploreMore";
import AdditionalLoanBank from "./pages/AdditionalLoanBank";
import AdditionalLoanCredit from "./pages/AdditionalLoanCredit";
import LoanApplicationUnable from "./pages/LoanApplicationUnable";
import FinalRejectState from "./pages/FinalRejectState";
import LoanAmountConfirmation from './pages/LoanAmountConfirmation';
import SelfiePhotoLocVerification from "./pages/SelfiePhotoLocVerificationV2";
import ShopOnlyPhotoLocVerification from "./pages/ShopOnlyPhotoLocVerification.tsx";
import ShopSelfiePhotoLocVerification from "./pages/ShopSelfiePhotoLocVerification.tsx";
import ShopVideoLocVerification from "./pages/ShopVideoLocVerification.tsx";
import ConsentRouting from "./pages/ConsentRouting";
import UploadScoreEngineResult from "./pages/UploadScoreEngineResult.tsx";
import LoanAmountWhitespace from "./pageswhitespace/LoanAmountWhitespace";
import JourneyError from "./pages/JourneyError";
import Grievance from "./pages/Grievance";

import SuccessWhitespace from "./pageswhitespace/SuccessWhitespace";
import RejectedWhitespace from "./pageswhitespace/RejectedWhitespace";
import MasDigioJourney from "./pages/MasDigioJourney";
import ESignJourney from "./pages/ESignJourney";
import DocPushJourney from "./pages/DocPushJourney";
import SelfieAadhaarMatch from "./pages/SelfieAadhaarMatch";
import FaceMatchUpload from "./pages/FaceMatchUpload";

import BankAccountAuthentication from "./pages/BankAccountAuthentication.tsx";
import SessionInfo from "./pages/SessionInfo";
import DigiCheck from "./pages/DigiCheck";
import DigioDigilockerKYCCallback from "./pages/DigioDigilockerKYCCallback";
import AdminDashboard from "./pagesadmin/AdminDashboard.tsx";
import AdminLogin from "./pagesadmin/AdminLogin.tsx";
import UploadLeads from "./pages/UploadLeads";
import { LoaderProvider, useLoader } from "./contexts/LoaderContext";
import FullScreenLoader from "./components/FullScreenLoader";
import ProtectedRoute from "./components/ProtectedRoute";
import JourneySync from "./components/JourneySync";
import { AuthProvider } from "@/contexts/AuthContext.tsx";
import { AdminProtectedRoute, InternalProtectedRoute } from "@/components/ProtectedRoute";
import MobileGuard from "@/components/MobileGuard";
import axios from '@/lib/axios';
import { API_BASE_URL } from './config';

import RequirementsAppJourney from "./pages/RequirementsAppJourney.tsx";
import LenderRejectionWait from "./pages/LenderRejectionWait";

import LMSLogin from "./LMS/pages/customer/LMSLogin";
import LMSStaffLogin from "./LMS/pages/staff/LMSStaffLogin";
import LMSDashboard from "./LMS/pages/customer/LMSDashboard";
import LMSStaffDashboard from './LMS/pages/staff/LMSStaffDashboard';
import LoanDetailsPage from './LMS/pages/customer/LoanDetailsPage';
import StaffTransactionsPage from './LMS/pages/staff/StaffTransactionsPage';
import StaffDataExplorer from './LMS/pages/staff/StaffDataExplorer';
import SimulationDashboard from './LMS/pages/staff/SimulationDashboard';
import GatewaySimulation from './LMS/pages/customer/GatewaySimulation';
import LMSProtectedRoute from './LMS/components/LMSProtectedRoute';
import CRMLeadForm from "./CRM/pages/CRMLeadForm.tsx";
import CRMLeadTable from "./CRM/pages/CRMLeadTable.tsx";
import CRMBulkUpload from "./CRM/pages/CRMBulkUpload.tsx";
import ScrollToTop from "./components/ScrollToTop";
import LoadingTransition from "./pages/LoadingTransition";
import JourneyRouter from "./pages/JourneyRouter";
import JourneyPage from "./pages/JourneyPage";
import BuilderPage from "./pages/BuilderPage";
import FlowBuilderPage from "./pages/FlowBuilderPage";
import HomePreApprovedGateway from "./pages/HomePreApprovedGateway";
import BasicLoanDetailsPage from "./pages/BasicLoanDetails.tsx";


const GlobalLoader = () => {
  const { loading, loaderMessage } = useLoader();
  return <FullScreenLoader loading={loading} message={loaderMessage} />;
};

const queryClient = new QueryClient();

const App = () => {
  const [minWidthEnforced, setMinWidthEnforced] = React.useState(false);

  // Show maintenance page when ?source=1 or ?source=2 is present in the URL
  const searchParams = new URLSearchParams(window.location.search);
  const source = searchParams.get("source");
  if (source === "4" || source === "1"|| source === "2"|| source === "3") {
    return <MaintenancePage />;
  }


  useEffect(() => {
    // Bootstrap CSRF token on app load
    const initCSRF = async () => {
      try {
        await axios.get(`${API_BASE_URL}/api/csrf/`);
      } catch (err) {
        console.error('CSRF bootstrap failed', err);
      }
    };
    initCSRF();

    // Subdomain detection and redirection logic
    // ops.* subdomain → always use staff login
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    if (hostname.startsWith('ops.') && pathname === '/lms/login') {
      window.location.href = '/lms/staff/login';
    }
    // NOTE: The previous guard that redirected /lms/staff/login → /lms/login on
    // non-myloan/uatil hostnames has been removed. Staff portal must be accessible
    // on all environments. Access control is enforced by the backend (is_staff check).
  }, []); // empty dependency → run once on mount

  // Enforce minimum width of 390px
  useEffect(() => {
    const enforceMinWidth = () => {
      const root = document.getElementById('root');
      if (root && window.innerWidth < 390) {
        root.style.minWidth = '390px';
        root.style.width = '390px';
        document.body.style.overflowX = 'auto';
      } else if (root) {
        root.style.minWidth = '390px';
        root.style.width = '100%';
        document.body.style.overflowX = 'hidden';
      }
      setMinWidthEnforced(true);
    };

    enforceMinWidth();
    window.addEventListener('resize', enforceMinWidth);
    
    return () => window.removeEventListener('resize', enforceMinWidth);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LoaderProvider>
        <TooltipProvider>
          <div
            style={{
              minWidth: '390px',
              width: Math.max(390, window.innerWidth) + 'px',
              overflowX: window.innerWidth < 390 ? 'auto' : 'hidden'
            }}
          >
            <Toaster />
            <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <JourneySync />
            <Routes>
              {/* Accessible from Desktop + Mobile both */}
              <Route path="/" element={<HomeGateway />} />
              {/* V3 Journey Builder: dynamic page renderer — catch-all for builder_app pages */}
              <Route path="/journey/:slug" element={<JourneyRouter />} />
              {/* Legacy journey page route - used by many components for navigation */}
              <Route path="/loan-application" element={<ProtectedRoute><JourneyPage /></ProtectedRoute>} />
              <Route path="/loading" element={<LoadingTransition />} />
              <Route path="/journey-error" element={<JourneyError />} />
              <Route path="/contact-support" element={<Grievance />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route
                path="/setup/upload/leads"
                element={
                  <InternalProtectedRoute>
                    <UploadLeads />
                  </InternalProtectedRoute>
                }
              />
              <Route
                path="/crm/leads"
                element={
                  <InternalProtectedRoute>
                    <CRMLeadForm />
                  </InternalProtectedRoute>
                }
              />
              <Route
                path="/crm/leads/:leadId"
                element={
                  <InternalProtectedRoute>
                    <CRMLeadForm />
                  </InternalProtectedRoute>
                }
              />
              <Route
                path="/admin/bulk-upload"
                element={
                  <InternalProtectedRoute>
                    <CRMBulkUpload />
                  </InternalProtectedRoute>
                }
              />
              <Route
                path="/debug/trackers"
                element={
                  <InternalProtectedRoute>
                    <VerificationDebug />
                  </InternalProtectedRoute>
                }
              />
              <Route
                path="/admin-dashboard/crm/leads/:leadId"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              {/*
                PHASE B5g port of v2 commit 31e683cd -- dedicated, shareable
                URL for "Disbursed Loan Details". The screen is an
                `activeView` inside AdminDashboard, so — exactly like the CRM
                lead route above — this renders the SAME dashboard and
                AdminDashboard forces the view from the pathname. That keeps
                one shell, one sidebar and one auth gate.
              */}
              <Route
                path="/admin-dashboard/lms/disbursed-loans"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin-dashboard"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/setup/upload/scoreengineresults"
                element={
                  <InternalProtectedRoute>
                    <UploadScoreEngineResult />
                  </InternalProtectedRoute>
                }
              />
              
              {/* Journey Builder Routes */}
              <Route
                path="/stitch/builder"
                element={
                  <InternalProtectedRoute>
                    <BuilderPage />
                  </InternalProtectedRoute>
                }
              />
              <Route
                path="/stitch/builder/:pageId"
                element={
                  <InternalProtectedRoute>
                    <BuilderPage />
                  </InternalProtectedRoute>
                }
              />
              <Route
                path="/stitch/flow-builder"
                element={
                  <InternalProtectedRoute>
                    <FlowBuilderPage />
                  </InternalProtectedRoute>
                }
              />

              {/* Pre-approved landing: animated splash → redirects to /v2/home/preapproved */}
              <Route path="/home/preapproved" element={<HomePreApprovedGateway />} />
 
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <HomeGateway />
                  </ProtectedRoute>
                }
              />

              {/* LMS Routes */}
              <Route path="/lms/login" element={<LMSLogin />} />
              <Route path="/lms/staff/login" element={<LMSStaffLogin />} />
              <Route path="/lms/dashboard" element={
                <LMSProtectedRoute>
                  <LMSDashboard />
                </LMSProtectedRoute>
              } />
              <Route path="/lms/gateway-sim" element={<GatewaySimulation />} />
              <Route path="/lms/staff/dashboard" element={
                <LMSProtectedRoute requireStaff>
                  <LMSStaffDashboard />
                </LMSProtectedRoute>
              } />
              <Route path="/lms/staff/transactions" element={
                <LMSProtectedRoute requireStaff>
                  <StaffTransactionsPage />
                </LMSProtectedRoute>
              } />
              <Route path="/lms/staff/data-explorer" element={
                <LMSProtectedRoute requireStaff>
                  <StaffDataExplorer />
                </LMSProtectedRoute>
              } />
              <Route path="/lms/staff/simulation" element={
                <LMSProtectedRoute requireStaff>
                  <SimulationDashboard />
                </LMSProtectedRoute>
              } />
              <Route path="/lms/loan/:loanId" element={
                <LMSProtectedRoute>
                  <LoanDetailsPage />
                </LMSProtectedRoute>
              } />

              {/* Accessible from Mobile only via MobileGuard */}
              <Route element={<MobileGuard />}>
                {/* Public routes */}
                <Route path="/home/:enc_id" element={<HomeGateway />} />
                <Route path="/home/:bapName" element={<Home />} />
                <Route path="/consent/:enc_id" element={<ConsentRouting />} />
                <Route path="/consent/:bapName" element={<ConsentPage />} />
                <Route path="/consent2/:enc_id" element={<ConsentRouting />} />
                <Route path="/loan-amount-confirmation" element={<ProtectedRoute><LoanAmountConfirmation /></ProtectedRoute>} />
                <Route path="/index" element={<IndexRouting />} />
                <Route path="/session-timeout" element={<SessionTimeout />} />

                {/* Protected routes */}
                <Route
                  path="/welcome"
                  element={
                    <ProtectedRoute>
                      <Welcome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kfs/:offerId"
                  element={
                    <ProtectedRoute>
                      <ProvisionKFS />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/home/:enc_id"
                  element={
                    <ProtectedRoute>
                      <HomeGateway />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/index/:enc_id"
                  element={
                    <ProtectedRoute>
                      <IndexRouting />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whitespace/index"
                  element={
                    <ProtectedRoute>
                      <IndexWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whitespace/home"
                  element={
                    <ProtectedRoute>
                      <HomeWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whitespace/consent"
                  element={
                    <ProtectedRoute>
                      <ConsentWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whitespace/curating-offers"
                  element={
                    <ProtectedRoute>
                      <CuratingOffersWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whitespace/pre-approved-amount"
                  element={
                    <ProtectedRoute>
                      <PreApprovedAmount />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-info"
                  element={
                    <ProtectedRoute>
                      <BusinessInfo />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whitespace/business-info"
                  element={
                    <ProtectedRoute>
                      <BusinessInfoWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/loan-amount"
                  element={
                    <ProtectedRoute>
                      <LoanAmountWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/success-whitespace"
                  element={
                    <ProtectedRoute>
                      <SuccessWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/rejected-whitespace"
                  element={
                    <ProtectedRoute>
                      <RejectedWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/requirements"
                  element={
                    <ProtectedRoute>
                      <RequirementsAppJourney />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/personal-info"
                  element={
                    <ProtectedRoute>
                      <PersonalInfo />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/terms"
                  element={
                    <Terms />
                  }
                />
                <Route
                  path="/privacy"
                  element={
                    <Privacy />
                  }
                />
                <Route
                  path="/contact-owner"
                  element={
                    <ProtectedRoute>
                      <ContactOwner />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/waiting-for-updates"
                  element={
                    <ProtectedRoute>
                      <WaitingForUpdates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/additional-details"
                  element={
                    <ProtectedRoute>
                      <AdditionalDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ruleset-a"
                  element={
                    <ProtectedRoute>
                      <InternalRulesA />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/all-offers"
                  element={
                    <ProtectedRoute>
                      <AllOffers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/loan-offer"
                  element={
                    <ProtectedRoute>
                      <LoanOffer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/additional-loan-gst"
                  element={
                    <ProtectedRoute>
                      <AdditionalLoanGST />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lender-decision"
                  element={
                    <ProtectedRoute>
                      <LenderMockDecision />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lender-simulation"
                  element={
                    <ProtectedRoute>
                      <LenderErrorSimulation />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lender-rejected-explore"
                  element={
                    <ProtectedRoute>
                      <LenderRejectedExploreMore />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lender-rejection-wait"
                  element={
                    <ProtectedRoute>
                      <LenderRejectionWait />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/additional-loan-bank"
                  element={
                    <ProtectedRoute>
                      <AdditionalLoanBank />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/additional-loan-credit"
                  element={
                    <ProtectedRoute>
                      <AdditionalLoanCredit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/final-offer"
                  element={
                    <ProtectedRoute>
                      <FinalOffer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bank-account-authentication"
                  element={
                    <ProtectedRoute>
                      <BankAccountAuthentication />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/setup-mandates"
                  element={
                    <ProtectedRoute>
                      <SetupMandates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ruleset-whitespace"
                  element={
                    <ProtectedRoute>
                      <InternalRulesWhitespace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ruleset-b"
                  element={
                    <ProtectedRoute>
                      <InternalRulesB />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/final-kfs"
                  element={
                    <ProtectedRoute>
                      <FinalKFS />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/loan-success"
                  element={
                    <ProtectedRoute>
                      <LoanSuccess />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/loan-application-unable"
                  element={
                    <ProtectedRoute>
                      <LoanApplicationUnable />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/final-reject-state"
                  element={
                    <ProtectedRoute>
                      <FinalRejectState />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/selfie-photo-loc-verification"
                  element={
                    <ProtectedRoute>
                      <SelfiePhotoLocVerification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shop-only-photo-loc-verification"
                  element={
                    <ProtectedRoute>
                      <ShopOnlyPhotoLocVerification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shop-selfie-photo-loc-verification"
                  element={
                    <ProtectedRoute>
                      <ShopSelfiePhotoLocVerification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shop-video-loc-verification"
                  element={
                    <ProtectedRoute>
                      <ShopVideoLocVerification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/session-info"
                  element={
                    <ProtectedRoute>
                      <SessionInfo />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/digi-check"
                  element={
                    <ProtectedRoute>
                      <DigiCheck />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mas-digio-journey"
                  element={
                    <ProtectedRoute>
                      <MasDigioJourney />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/esign"
                  element={
                    <ProtectedRoute>
                      <ESignJourney />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/doc-push"
                  element={
                    <ProtectedRoute>
                      <DocPushJourney />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kyc-digio-digilocker/callback"
                  element={
                    <ProtectedRoute>
                      <DigioDigilockerKYCCallback />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/selfie-aadhaar-match"
                  element={
                    <ProtectedRoute>
                      <SelfieAadhaarMatch />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/basic-loan-details"
                  element={<BasicLoanDetailsPage />}
                />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <GlobalLoader />
          </div>
        </TooltipProvider>
      </LoaderProvider>
    </QueryClientProvider>
  )
};

export default App;